<script lang="ts">
	import { localizeHref } from '@/paraglide/runtime';
</script>

<svelte:head>
	<title>Política de Privacidad | Zarpar</title>
	<meta
		name="description"
		content="Política de privacidad y protección de datos de Zarpar. Conoce cómo tratamos tus datos personales, tus derechos y el cumplimiento del RGPD en nuestra plataforma náutica."
	/>
</svelte:head>

<div class="mx-auto max-w-3xl px-2 py-14 md:px-0">
	<div class="max-w-150 relative mx-auto pb-24 text-center">
		<h1 class="text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl">
			Política de privacidad
		</h1>
		<p class="mt-4 text-base leading-7 text-slate-600">Ultima actualización 01/09/2025</p>
	</div>

	<div
		class="prose prose-sm prose-slate prose-a:font-semibold prose-a:text-sky-500 hover:prose-a:text-sky-600 max-w-160 mx-2 md:mx-0"
	>
		<ol class="list-inside list-decimal space-y-2">
			<li class="text-lg font-semibold">Objeto</li>
			<p>
				En cumplimiento de lo dispuesto en el Reglamento General de Protección de datos, la Ley
				Orgánica 3/2018, de 5 de diciembre de Protección de datos Personales y garantía de los
				derechos digitales, y en la Ley 34/2002, de 11 de julio, de servicios de la sociedad de la
				información y comercio electrónico, se informa al Usuario del sitio web de que al utilizar
				<a
					href="https://zarpar.eu"
					target="_blank"
					class="text-sky-500 underline hover:text-sky-600">https://zarpar.eu</a
				> (en adelante, la "página web") y/o comunicarse con ZARPAR, se podrán recoger y tratar datos
				personales de cada Usuario.
			</p>
			<p>
				El tratamiento de estos datos personales se rige por la presente Política de Privacidad, que
				hemos elaborado de acuerdo con la normativa de protección de datos aplicable.
			</p>
			<p>
				Recomendamos que leas detenidamente esta Política de Privacidad antes de utilizar la página
				web.
			</p>

			<li class="mt-4 text-lg font-semibold">Responsable del tratamiento</li>

			<p>
				El responsable del tratamiento de los datos que se recojan como consecuencia del uso y
				acceso de la página web es <strong>PLATAFORMA NÁUTICA DIGITAL S.L.</strong>, con NIF
				B19964188, y domicilio a efectos de notificaciones en C/ Pi y Margall, 18 – 2ºC, CP 36202 en
				Vigo, Pontevedra (en adelante, "<strong>ZARPAR</strong>").
			</p>
			<p>
				Para cualquier cuestión que precises relativa a protección de datos podrás contactar con
				ZARPAR por los siguientes medios:
			</p>
			<ul class="ml-4 list-inside list-disc">
				<li>Dirección postal: C/ Pi y Margall, 18 – 2ºC, CP 36202 Vigo (Pontevedra)</li>
				<li>Correo electrónico: <a href="mailto:info@zarpar.eu">info@zarpar.eu</a></li>
			</ul>

			<li class="mt-4 text-lg font-semibold">Datos recogidos</li>

			<p>
				Por el mero hecho de visitar y navegar por la página web de ZARPAR se pueden recoger datos
				como la dirección IP, el sistema operativo, tipo de dispositivo, etc. Estos datos no
				permiten la identificación del Usuario salvo que se cuente con información adicional
				proporcionada por otros medios. La recogida de estos datos a través de Cookies podrá
				deshabilitarse en el banner que aparece al acceder a la página web. Para más información,
				puedes consultar nuestra <a
					href={localizeHref('/politica-de-cookies')}
					class="text-sky-500 underline hover:text-sky-600">Política de Cookies</a
				>.
			</p>

			<h3 class="text-lg font-semibold italic">Alta usuario</h3>
			<p>
				Sin perjuicio de lo anterior, algunos de los servicios de ZARPAR requieren del alta de
				usuario, en cuyo caso tendrás que seguir un proceso de registro de usuario que podrás
				realizar utilizando tu perfil de usuario de Google o Apple, o manualmente.
			</p>
			<p>
				En el caso de que realices el proceso de alta de usuario mediante tu perfil de Google o
				Apple, ZARPAR sólo tendrá acceso a los datos que te identifican tales como tu nombre,
				dirección de correo electrónico e imagen de perfil. Para conocer en detalle los datos
				compartidos al iniciar sesión con estas cuentas puedes consultar la Política de Privacidad
				de <a
					href="https://support.google.com/accounts/answer/12921417?hl=es"
					target="_blank"
					class="text-sky-500 underline hover:text-sky-600">Google</a
				>
				o
				<a
					href="https://www.apple.com/legal/privacy/es//"
					target="_blank"
					class="text-sky-500 underline hover:text-sky-600">Apple</a
				>.
			</p>
			<p>
				En caso de que realices el alta de usuario en la página web manualmente, te solicitaremos
				los siguientes datos:
			</p>

			<p>ARMADORES:</p>
			<ul>
				<li>Nombre y apellidos</li>
				<li>Email</li>
				<li>Teléfono</li>
				<li>DNI</li>
				<li>Fecha de nacimiento</li>
				<li>IBAN</li>
			</ul>

			<p>PASAJEROS:</p>
			<ul>
				<li>Nombre y apellidos</li>
				<li>Email</li>
				<li>Teléfono</li>
				<li>DNI</li>
				<li>Titulación náutica</li>
			</ul>

			<p>
				Además, una vez hecha el alta de usuario podrás acceder a tu cuenta de usuario en la página
				web de ZARPAR. Dentro de la misma, en el apartado "Cuenta" del menú de la izquierda podrás
				completar tus datos personales con los siguientes datos necesarios para la facturación:
			</p>
			<ul>
				<li>DNI o NIF</li>
				<li>Empresa</li>
				<li>Dirección de facturación</li>
			</ul>

			<h3 class="text-lg font-semibold italic">Pago de los servicios</h3>
			<p>
				El pago de los servicios se hará a través de la pasarela de pago Stripe: ZARPAR no tendrá
				acceso a los datos de pago tales como el número de tarjeta de crédito o débito.
			</p>

			<h3 class="text-lg font-semibold italic">Formulario Suscríbete</h3>
			<p>
				Mediante este formulario podrás suscribirte a la newsletter de ZARPAR. Para ello te
				solicitaremos los siguientes datos personales: Email.
			</p>
			<p>
				Para enviar este formulario deberás aceptar previamente la Política de Privacidad, así como
				separadamente la suscripción a la newsletter.
			</p>

			<li class="mt-4 text-lg font-semibold">Veracidad, exactitud y completitud de los datos</li>
			<p>
				El Usuario garantiza que los datos personales que facilite durante el uso de la página web
				son veraces, exactos, completos y se encuentran actualizados, siendo responsable de
				cualquier daño o perjuicio, directo o indirecto, que pudiera ocasionarse como consecuencia
				del incumplimiento de tal obligación.
			</p>
			<p>
				El Usuario se obliga a comunicar a ZARPAR de forma inmediata cualquier modificación de sus
				datos personales que resulte relevante, a fin de que la información esté en todo momento
				actualizada y no contenga errores.
			</p>
			<p>De acuerdo con lo anterior, ZARPAR no será responsable si:</p>
			<ul class="ml-4 list-inside list-disc">
				<li>
					Los usuarios no son mayores de edad y/o no tienen capacidad legal para contratar los
					Servicios ofrecidos en la página web.
				</li>
				<li>
					ZARPAR ha recibido los datos personales y/o información privada de una persona adulta, que
					no es la misma que está utilizando la página web, sin haber obtenido esta última el
					consentimiento de dicha tercera persona para comunicarnos sus datos personales.
				</li>
				<li>
					Por circunstancias que están más allá de nuestro control razonable, como el hecho de que
					se nos haya proporcionado información errónea, incompleta y/o fraudulenta por parte del
					Usuario.
				</li>
			</ul>

			<li class="mt-4 text-lg font-semibold">Finalidades</li>
			<p>
				Las finalidades de la recogida y tratamiento de estos datos personales a través de la página
				web son las siguientes:
			</p>
			<ul>
				<li>
					Alta usuario: prestar los servicios propios de ZARPAR, mantener comunicaciones propias de
					la prestación de los servicios y emitir y enviar las correspondientes facturas.
				</li>
				<li>
					Formularios de contacto: Contestar peticiones de contacto de usuarios de la página web,
					resolver dudas y/o facilitar la información requerida, resolver quejas y/o reclamaciones,
					así como hacer seguimiento de las solicitudes.
				</li>
				<li>
					Formulario Suscríbete: Enviar periódicamente la newsletter u otras comunicaciones
					informativas o comerciales a aquellos usuarios que se suscriban.
				</li>
				<li>
					Analizar estadísticamente de la navegación en la página web para optimizar y mejorar la
					experiencia del Usuario.
				</li>
			</ul>

			<li class="mt-4 text-lg font-semibold">Base de legitimación</li>
			<p>
				Los datos suministrados a través de la página web se tratarán en base al consentimiento del
				Usuario, otorgado voluntariamente con la navegación, el alta de usuario o el envío de
				formularios de contacto, quejas y reclamaciones o suscripción a la newsletter.
			</p>

			<li class="mt-4 text-lg font-semibold">Conservación</li>
			<p>
				Trataremos la información personal facilitada durante el tiempo necesario para prestar los
				servicios solicitados y tramitar su facturación y cobro, tramitar las solicitudes de
				contacto y hacer seguimiento de las mismas.
			</p>
			<p>
				Una vez finalizado este periodo, ZARPAR podrá conservar los datos personales bloqueados,
				durante los plazos legalmente previstos durante los que se pudieran derivar eventuales
				responsabilidades.
			</p>

			<li class="mt-4 text-lg font-semibold">Destinatarios</li>
			<p>ZARPAR no cederá tus datos personales a terceros, salvo en los siguientes supuestos:</p>
			<ul class="ml-4 list-inside list-disc space-y-2">
				<li>
					En caso de que uses nuestro servicio de Experiencias o de alquiler de embarcaciones: para
					poder prestar el servicio, facilitamos tus datos de contacto al Armador del barco que
					elijas (nombre, apellidos y email).
				</li>
				<li>
					Proveedores de servicios tecnológicos que albergan nuestros sistemas de información y/o
					que nos ofrecen herramientas o apoyo esenciales para ejecutar el servicio que ofrecemos.
					Estas terceras partes solo tienen acceso a la información personal que necesitan para
					llevar a cabo dichos servicios. Se les exige que mantengan en confidencialidad su
					información personal y no pueden utilizarla de ninguna otra forma que aquella que les
					hemos solicitado. Todas estas empresas aplican el mismo grado de protección que ZARPAR, y
					el acceso a los datos personales está regulado por un contrato de encargado de
					tratamiento.
				</li>
				<li>
					Organismos públicos y/o las autoridades administrativas o judiciales, así como la policía,
					siempre que lo exijan las leyes y disposiciones aplicables.
				</li>
				<li>
					Terceros con el consentimiento del usuario u otra base legítima: En el caso de que
					queramos compartir datos con terceros fuera del ámbito de esta Política de privacidad
					solicitaremos, en todo caso, tu consentimiento o serás informado sobre la misma y su base
					legítima.
				</li>
			</ul>
			<p>
				Estos terceros tienen su sede en la Unión Europea, por lo que los datos personales no serán
				objeto de una transferencia internacional. En el caso de que fuera necesario realizar una
				transferencia internacional de datos, te informamos que sólo transferiremos tus datos
				personales a aquellas entidades/organismos situados en países que ofrezcan un nivel de
				protección de datos personales equitativo al que establecen las leyes, reglamentos y
				disposiciones en materia de protección de datos de los países europeos o, en su defecto,
				aplicaremos las garantías adecuadas exigidas conforme la legislación vigente en materia de
				protección de datos personales, con el fin de garantizar la privacidad y la seguridad de tus
				datos personales y que tus derechos y libertades individuales estén debidamente
				garantizados.
			</p>
			<p>
				Te recordamos que esta Política de Privacidad sólo se refiere a la recogida, tratamiento y
				uso de información (relativa a datos personales) por ZARPAR a través de la interacción con
				nuestra página web.
			</p>

			<li class="mt-4 text-lg font-semibold">Derechos de los interesados</li>
			<p>
				Los interesados podrán ejercer los derechos de acceso, rectificación, supresión,
				portabilidad y la limitación u oposición, dirigiéndose por escrito, de forma que permita
				acreditar su identidad a la dirección postal de ZARPAR (C/ Pi y Margall, 18 – 2ºC, CP 36202
				en Vigo, Pontevedra) o bien a la siguiente dirección de correo electrónico: <a
					href="mailto:info@zarpar.eu"
					class="text-sky-500 underline hover:text-sky-600">info@zarpar.eu</a
				>
			</p>
			<p>
				ZARPAR responderá a las peticiones en el plazo máximo de un mes desde la recepción de tu
				solicitud. Este plazo podrá prorrogarse en caso necesario, atendiendo a la complejidad y el
				número de solicitudes, en cuyo caso ZARPAR informará al interesado de la prórroga dentro del
				primer mes desde la solicitud.
			</p>
			<p>
				El Usuario podrá presentar reclamación ante la Agencia Española de Protección de Datos (<a
					href="https://www.aepd.es"
					target="_blank"
					rel="noopener noreferrer">www.aepd.es</a
				>) en materia de protección de datos, especialmente cuando no haya obtenido satisfacción en
				el ejercicio de sus derechos.
			</p>
		</ol>
	</div>
</div>
